import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  PortalTableCell,
  PortalTableHead,
  PortalTableRow,
} from '../../../../styles/portalListTable';
import { PORTAL_ACCENT } from '../../../../styles/portalTheme';
import EventApprovalStatusBadge from '../../../Events/widgets/EventApprovalStatusBadge';
import EventRowActions from '../../../Events/widgets/EventRowActions';
import {
  canSubmitPartnerEvent,
  canShowPartnerEventEdit,
} from '../../../../contexts/EventsScopeContext';
import {
  canArchivePartnerEvent,
  formatPartnerEventArchivedAt,
  isPartnerEventArchived,
} from '../partnerEventArchive';

const EventThumbnail = styled('img')({
  width: 48,
  height: 48,
  borderRadius: 8,
  flexShrink: 0,
  objectFit: 'cover',
  display: 'block',
  backgroundColor: '#1a1a2e',
});

const PartnerEventsListTable = ({
  events,
  onView,
  onEdit,
  onArchive,
  onSubmit,
  submittingEventId = null,
  features = {},
  viewOnlyRowActions = false,
  containerSx,
}) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{ borderRadius: 2, boxShadow: 'none', ...containerSx }}
  >
    <Table stickyHeader>
      <PortalTableHead>
        <TableRow>
          <TableCell>Event Details</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Date &amp; Location</TableCell>
          <TableCell>Capacity</TableCell>
          <TableCell>RSVPs</TableCell>
          <TableCell>Paid</TableCell>
          <TableCell>Att. %</TableCell>
          <TableCell>Revenue</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </PortalTableHead>
      <TableBody>
        {events.map((event) => {
          const archived = isPartnerEventArchived(event);
          const showArchive = !viewOnlyRowActions && canArchivePartnerEvent(event);
          const showEdit = !viewOnlyRowActions && canShowPartnerEventEdit(event, features);
          const archivedLabel = formatPartnerEventArchivedAt(event.deletedAt);

          return (
            <PortalTableRow
              key={event.id}
              sx={
                archived
                  ? {
                      bgcolor: 'background.default',
                      '& .MuiTableCell-root': { color: 'text.secondary' },
                    }
                  : undefined
              }
            >
              <PortalTableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {event.imageUrl ? (
                    <EventThumbnail src={event.imageUrl} alt="" loading="lazy" />
                  ) : (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: '#1a1a2e',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: archived ? 'text.secondary' : 'text.primary',
                        lineHeight: 1.35,
                      }}
                    >
                      {event.title}
                    </Typography>
                    {archived && archivedLabel ? (
                      <Typography
                        sx={{
                          color: '#BF360C',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          lineHeight: 1.3,
                          mt: 0.25,
                          whiteSpace: 'normal',
                        }}
                      >
                        Archived on {archivedLabel}
                      </Typography>
                    ) : null}
                    {event.typeLabel ? (
                      <Typography
                        sx={{
                          color: '#C45C2a',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          lineHeight: 1.3,
                          mt: 0.25,
                        }}
                      >
                        {event.typeLabel}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </PortalTableCell>
              <PortalTableCell>
                <EventApprovalStatusBadge status={event.approvalStatus} />
              </PortalTableCell>
              <PortalTableCell>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                  {event.dateLine}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  {event.locationLine}
                </Typography>
              </PortalTableCell>
              <PortalTableCell>{event.totalSeats ?? '—'}</PortalTableCell>
              <PortalTableCell>{event.rsvpSeats ?? '—'}</PortalTableCell>
              <PortalTableCell>{event.paidSeats ?? '—'}</PortalTableCell>
              <PortalTableCell>{event.attendancePercent ?? '—'}</PortalTableCell>
              <PortalTableCell sx={{ fontWeight: 500 }}>{event.revenue ?? '—'}</PortalTableCell>
              <PortalTableCell>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {!viewOnlyRowActions && !archived && canSubmitPartnerEvent(event) && onSubmit ? (
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      loading={submittingEventId === event.id}
                      onClick={() => onSubmit(event.id)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1,
                        color: PORTAL_ACCENT,
                        borderColor: PORTAL_ACCENT,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Submit
                    </LoadingButton>
                  ) : null}
                  <EventRowActions
                    eventId={event.id}
                    onView={onView}
                    onEdit={
                      viewOnlyRowActions ? undefined : showEdit && onEdit ? onEdit : undefined
                    }
                    onArchive={
                      viewOnlyRowActions
                        ? undefined
                        : showArchive && onArchive
                          ? onArchive
                          : undefined
                    }
                  />
                </Box>
              </PortalTableCell>
            </PortalTableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

export default PartnerEventsListTable;
